import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, IStatement, Navbar } from "../../components";
import DockedControls from "./components/DockedControls/DockedControls";
import multistepForm from "../../hooks/MultistepForm";
import { getFormConfig, INITIAL_DATA } from "./form-config";
import IkhokhaLoader from "../../assets/iK-Load-Animation.gif";
import { CnpForm } from "../../types";
import Stepper from "../../components/Stepper/Stepper";
import { createInvoice, getMerchantDetails, updateInvoice, updateInvoiceToUnpaid } from "../../api/statement-service/requests";
import Share from "./Share/Share";
import { Modal } from "../../components/Modal/Modal";
import styles from "./index.module.scss";
import { clearSessionStorage, getFromSessionStorage, setSessionStorageData, StorageKeys } from "../../utils/Storage";
import { isHttpErrorResponse } from "../../api/util";
import { setNavigateOptions } from "../../utils/utils";
import { setMerchantDetails } from "../../redux/reducer/authentication";
import { authErrorHeader, authErrorMessage, merchantErrorHeader, merchantErrorMessage } from "../../utils/ErrorMessages";

const AppContainer = () => {
  const existingFormDataString = getFromSessionStorage(StorageKeys.STATEMENT_DATA);
  const initialData = existingFormDataString ? JSON.parse(existingFormDataString) : INITIAL_DATA;
  const initialInvoiceDetails = { id: "", createDate: "" };
  const currentStepIndexString = getFromSessionStorage(StorageKeys.CURRENT_STEP_INDEX);
  const currentStepToJumpTo = currentStepIndexString ? parseInt(currentStepIndexString, 10) : 0;
  const [data, setData] = useState(initialData);
  const [invoiceDetails, setInvoiceDetails] = useState(initialInvoiceDetails);
  const [canProceed, setCanProceed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const dispatch = useDispatch();
  const merchant = `${useSelector(
    // @ts-ignore
    state => state.authenticationReducer.loggedInMerchant,
  )}`;

  const updateData = (fields: Partial<CnpForm>) => {
    setData((prev: CnpForm) => {
      const updatedFormData = { ...prev, ...fields };
      setSessionStorageData(StorageKeys.STATEMENT_DATA, JSON.stringify(updatedFormData));
      return updatedFormData;
    });
  };
  const navigate = useNavigate();
  const confirmProceed = (val: boolean) => {
    setCanProceed(val);
  };
  const skipTo = (index: number) => {
    // eslint-disable-next-line no-use-before-define
    goTo(index);
  };

  const { steps, currentStepIndex, step, back, next, goTo, isReviewStep, isCustomerDetailsStep } = multistepForm(
    // eslint-disable-next-line no-use-before-define
    getFormConfig(data, confirmProceed, updateData, skipTo),
    currentStepToJumpTo,
  );

  setSessionStorageData(StorageKeys.CURRENT_STEP_INDEX, currentStepIndex.toString());

  const formSteps = steps.filter(s => s.isFormStepperStep);
  const performNextAction = () => {
    const { id, createDate } = invoiceDetails;
    setCanProceed(false);
    updateInvoice(merchant, id, createDate, data).then();
    if (isReviewStep()) {
      setShowShare(true);
    } else {
      next();
    }
  };

  const loggedInMerchant = `${useSelector(
    // @ts-ignore
    state => state.authenticationReducer.loggedInMerchant,
  )}`;

  const shareDoneAction = () => {
    setShowShare(false);
    clearSessionStorage();
    navigate("/");
  };

  const performBackAction = () => {
    if (currentStepIndex === 0) {
      navigate("/");
    }
    back();
  };

  const onShare = async () => {
    const saveErrorHeader = "Save Failed";
    const saveErrorMessage = "Failed to save Invoice information, please try again";
    const { invoiceNumber } = data.paymentTerms;
    const response = await updateInvoiceToUnpaid(merchant, invoiceDetails.id, invoiceDetails.createDate, {
      ...data,
      paymentTerms: {
        ...data.paymentTerms,
        invoiceNumber: invoiceNumber || getFromSessionStorage(StorageKeys.INVOICE_REFERENCE),
      },
    });

    if (isHttpErrorResponse(response)) {
      navigate(`/error`, {
        state: setNavigateOptions(true, "cnp", saveErrorHeader, saveErrorMessage),
      });
    }
  };

  useEffect(() => {
    if (!getFromSessionStorage(StorageKeys.INVOICE_DETAILS)) {
      createInvoice(merchant).then(invoice => {
        const { id, createDate } = invoice;
        setSessionStorageData(StorageKeys.INVOICE_DETAILS, JSON.stringify({ id, createDate }));
        setInvoiceDetails({ id, createDate }); // required for updating
      });
    } else {
      const invoiceDetailsString = getFromSessionStorage(StorageKeys.INVOICE_DETAILS);
      setInvoiceDetails(invoiceDetailsString ? JSON.parse(invoiceDetailsString) : initialInvoiceDetails);
    }
    getMerchantDetails(loggedInMerchant).then(merchant => {
      setLoading(false);
      if (isHttpErrorResponse(merchant)) {
        if (merchant.statusCode === 401) {
          navigate(`/error`, {
            state: setNavigateOptions(false, "", authErrorHeader, authErrorMessage),
          });
        } else {
          navigate(`/error`, {
            state: setNavigateOptions(true, "cnp", merchantErrorHeader, merchantErrorMessage),
          });
        }
        return;
      }
      const { ownerInfo } = merchant;
      dispatch(setMerchantDetails(ownerInfo));
    });
  }, []);
  return loading ? (
    <div>
      <div className={styles.LoaderContainer}>
        <img className="loaderImage customIcon" src={IkhokhaLoader} alt="iKhokha Loader" />
      </div>
    </div>
  ) : (
    <div className={styles.zUp}>
      <Navbar title={step.title} />
      <div className={styles.MainContent}>
        <Card className={styles.FormCard}>
          <div className={styles.Intro}>
            <div>
              <IStatement />
            </div>
            <div>
              <div className={styles.IntroHeading}>Create Invoice</div>
              <div className={styles.IntroDescription}>
                Create an itemised invoice and send it to your customers via email or WhatsApp.
              </div>
            </div>
          </div>
          <div className={`${styles.HorizontalRule} w-100`} />
          {!isReviewStep() && <Stepper activeStep={currentStepIndex} steps={formSteps} />}
          <form>{step.component}</form>
          <div className={`${styles.HorizontalRule} w-100`} />
          <div className={styles.ActionButtonContainer}>
            <Button size="small" label={step.previousButtonText} onClick={performBackAction} varient="secondary" />
            <Button size="small" label={step.nextButtonText} onClick={performNextAction} disabled={!canProceed} />
          </div>
          <Modal
            content={
              <Share
                onDone={() => shareDoneAction()}
                invoiceId={invoiceDetails.id}
                invoiceCreateDate={invoiceDetails.createDate}
                onShare={onShare}
              />
            }
            title="Share iK Invoice"
            isOpen={showShare}
          />
        </Card>
      </div>
      {!isCustomerDetailsStep() && (
        <DockedControls
          data={data}
          step={step}
          next={performNextAction}
          back={performBackAction}
          showLineItemTotals={!!step.showDockTotals}
          canProceed={canProceed}
        />
      )}
    </div>
  );
};

export default AppContainer;
